import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import AtomIcon, {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import {useSuiContext} from '@s-ui/react-context'
import PrimitiveTypography, {
  primitiveTypographyDesign,
  primitiveTypographyFontSize,
  primitiveTypographyFontWeight
} from '@s-ui/react-primitive-typography'
import IconChevronRight from '@adv-mt/mt-svg-icons/lib/ChevronRight'
import IconInfoOutline from '@adv-mt/mt-svg-icons/lib/InfoOutline'
import IconNoteEditOutline from '@adv-mt/mt-svg-icons/lib/NoteEditOutline'

const BASE_CLASS = 'mt-NetChatHistoryList'

const NetChatHistoryList = ({chats = [], currentChatId, onSelectChat, onNewChat}) => {
  const {domain, i18n} = useSuiContext()

  const config = domain.get('config')
  const {CHAT_HISTORY_TTL_DAYS} = config.get('COMMON').AI_ASSISTANT
  const urlStatics = config.get('urlStatics')

  return (
    <div className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-actions`}>
        <AtomButton
          fullWidth
          design={atomButtonDesigns.OUTLINE}
          shape={atomButtonShapes.CIRCULAR}
          onClick={onNewChat}
          leftIcon={
            <AtomIcon size={ATOM_ICON_SIZES.medium}>
              <IconNoteEditOutline />
            </AtomIcon>
          }
        >
          {i18n.t('NET.NEW_CHAT.HEADER_TITLE')}
        </AtomButton>
      </div>

      {chats.length > 0 && (
        <>
          <div className={`${BASE_CLASS}-section`}>
            <PrimitiveTypography
              design={primitiveTypographyDesign.HEADLINE_2}
              fontWeight={primitiveTypographyFontWeight.SEMI_BOLD}
              fontSize={primitiveTypographyFontSize.XL}
            >
              {i18n.t('NET.HISTORIC.RECENTS')}
            </PrimitiveTypography>
            <div className={`${BASE_CLASS}-sectionSubtitle`}>
              <AtomIcon size={ATOM_ICON_SIZES.small}>
                <IconInfoOutline />
              </AtomIcon>
              <PrimitiveTypography design={primitiveTypographyDesign.BODY_2} fontSize={primitiveTypographyFontSize.M}>
                {i18n.t('NET.HISTORIC.DELETE_RECENTS_MESSAGE', {days: CHAT_HISTORY_TTL_DAYS})}
              </PrimitiveTypography>
            </div>
          </div>

          <ul className={`${BASE_CLASS}-list`}>
            {chats.map(chat => (
              <li
                key={chat.id}
                className={`${BASE_CLASS}-item${chat.id === currentChatId ? ` ${BASE_CLASS}-item--active` : ''}`}
              >
                <button className={`${BASE_CLASS}-itemButton`} onClick={() => onSelectChat(chat.id)}>
                  <span className={`${BASE_CLASS}-itemTitle`}>{chat.title || i18n.t('NET.CHAT_HISTORY.UNTITLED')}</span>
                  <div className={`${BASE_CLASS}-itemIcons`}>
                    {chat.loading && <div className={`${BASE_CLASS}-itemLoadingSpinner`} />}
                    <AtomIcon size={ATOM_ICON_SIZES.medium}>
                      <IconChevronRight />
                    </AtomIcon>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {!chats.length && (
        <div className={`${BASE_CLASS}-empty`}>
          <img
            alt=""
            aria-hidden="true"
            className={`${BASE_CLASS}-emptyIllustration`}
            src={`${urlStatics}images/illustrations/chat-message-bubbles.svg`}
          />
          <PrimitiveTypography
            design={primitiveTypographyDesign.HEADLINE_2}
            fontWeight={primitiveTypographyFontWeight.SEMI_BOLD}
            fontSize={primitiveTypographyFontSize.XXL}
            className={`${BASE_CLASS}-emptyTitle`}
          >
            {i18n.t('NET.CHAT_HISTORY.EMPTY_TITLE')}
          </PrimitiveTypography>
          <PrimitiveTypography design={primitiveTypographyDesign.BODY_1} className={`${BASE_CLASS}-emptyDescription`}>
            {i18n.t('NET.CHAT_HISTORY.EMPTY_DESCRIPTION')}
          </PrimitiveTypography>
        </div>
      )}
    </div>
  )
}

NetChatHistoryList.displayName = 'NetChatHistoryList'

NetChatHistoryList.propTypes = {
  chats: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      loading: PropTypes.bool
    })
  ),
  currentChatId: PropTypes.string,
  onSelectChat: PropTypes.func.isRequired,
  onNewChat: PropTypes.func.isRequired
}

export default NetChatHistoryList
